import React, { useRef, useState, useLayoutEffect, useId, useCallback } from 'react';
import { findPhotoFrame, getCropGeometry, clampSourceCrop } from './photoCropGeometry';

export default function PhotoCropper({ photoUrl, cardDesign, onCropChange, onReadyChange }) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const geometryRef = useRef(null);
  const cropRef = useRef(null);
  const pointers = useRef(new Map());
  const start = useRef(null);
  const measureRef = useRef(() => {});
  const callbacks = useRef({ onCropChange, onReadyChange });
  callbacks.current = { onCropChange, onReadyChange };
  const [view, setView] = useState(null);
  const [failed, setFailed] = useState(false);
  const hintId = useId();
  const frame = findPhotoFrame(cardDesign);
  const aspect = frame ? frame.width / frame.height : null;

  const update = useCallback(crop => {
    const geometry = geometryRef.current;
    if (!geometry) return;
    const next = clampSourceCrop(crop, geometry);
    cropRef.current = next;
    setView({ geometry, crop: next });
    // Synchronous notification: Use Photo can follow a pointer event immediately.
    callbacks.current.onCropChange?.(next);
    callbacks.current.onReadyChange?.(true);
  }, []);

  useLayoutEffect(() => {
    geometryRef.current = null;
    cropRef.current = null;
    pointers.current.clear();
    start.current = null;
    setView(null);
    setFailed(false);
    callbacks.current.onReadyChange?.(false);
    const measure = () => {
      const image = imgRef.current;
      const box = containerRef.current?.getBoundingClientRect();
      if (!image?.complete || !box) return;
      const geometry = getCropGeometry(box.width, box.height, aspect, image.naturalWidth, image.naturalHeight);
      if (!geometry) return;
      geometryRef.current = geometry;
      pointers.current.clear();
      start.current = null;
      update(cropRef.current || geometry.centerCrop);
    };
    measureRef.current = measure;
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (containerRef.current) observer?.observe(containerRef.current);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      measureRef.current = () => {};
    };
  }, [photoUrl, aspect, update]);

  function rebase() {
    start.current = pointers.current.size === 1 && cropRef.current
      ? { pointer: [...pointers.current.values()][0], crop: cropRef.current }
      : null;
  }

  function onPointerDown(event) {
    if (!view || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    rebase();
  }

  function onPointerMove(event) {
    if (!pointers.current.has(event.pointerId)) return;
    event.preventDefault();
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    // Zoom deferred until current production renderer cropScale semantics are verified.
    // Multiple touches pause dragging; one remaining touch establishes a fresh origin.
    if (pointers.current.size !== 1 || !start.current) return;
    const { pointer, crop } = start.current;
    const scale = geometryRef.current.displayScale;
    update({ x: crop.x - (event.clientX - pointer.x) / scale, y: crop.y - (event.clientY - pointer.y) / scale });
  }

  function onPointerEnd(event) {
    if (!pointers.current.delete(event.pointerId)) return;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    rebase();
  }

  function reset() {
    if (!geometryRef.current) return;
    update(geometryRef.current.centerCrop);
    rebase();
  }

  function onKeyDown(event) {
    const direction = { ArrowLeft: [1, 0], ArrowRight: [-1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1] }[event.key];
    if (!direction || !geometryRef.current) return;
    event.preventDefault();
    const step = 10 / geometryRef.current.displayScale;
    update({ x: cropRef.current.x + direction[0] * step, y: cropRef.current.y + direction[1] * step });
    rebase();
  }

  const geometry = view?.geometry;
  return (
    <section className="photo-adjustment" aria-label="Photo adjustment">
      <p id={hintId}>Drag to reposition • Use arrow keys on a keyboard</p>
      {!frame ? <p role="alert">Photo frame unavailable. Retake after the card design has loaded.</p> : (
        <div ref={containerRef} className="photo-crop-area" tabIndex={0}
          role="group" aria-label="Reposition photo" aria-describedby={hintId}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd} onPointerCancel={onPointerEnd}
          onLostPointerCapture={onPointerEnd} onKeyDown={onKeyDown}>
          <img ref={imgRef} key={photoUrl} src={photoUrl} alt="Captured preview" draggable={false}
            onLoad={() => measureRef.current()}
            onError={() => {
              geometryRef.current = null;
              cropRef.current = null;
              setView(null);
              setFailed(true);
              callbacks.current.onReadyChange?.(false);
            }}
            style={geometry ? {
              visibility: 'visible', width: imgRef.current.naturalWidth, height: imgRef.current.naturalHeight,
              transform: `translate(${geometry.frameX - view.crop.x * geometry.displayScale}px, ${geometry.frameY - view.crop.y * geometry.displayScale}px) scale(${geometry.displayScale})`
            } : { visibility: 'hidden' }} />
          {geometry && <div className="photo-crop-frame" aria-hidden="true" style={{
            left: geometry.frameX, top: geometry.frameY, width: geometry.frameW, height: geometry.frameH
          }} />}
        </div>
      )}
      {frame && !view && !failed && <p role="status">Preparing photo adjustment…</p>}
      {failed && <p role="alert">Photo could not be loaded. Please retake it.</p>}
      <button type="button" className="photo-position-reset" disabled={!view || failed} onClick={reset}>Reset Position</button>
    </section>
  );
}
